// Client for business-transfer-service — store sales and changes of owner,
// tracked like an order (see journeyApi.ts for the sibling pattern this
// mirrors: same shared `platform` cluster, base-path routed).

import type {
  AdminTransferDetail, AdminTransferList, CreateTransferRequest, LinkDeviceRequest, ScheduleTransferRequest,
  RunNowRequest, UpdateOptionsRequest, VarSheetRequest, TransferStatus,
} from '@platform/shared-types';

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

export function resolveHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/business-transfer-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/business-transfer`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/business-transfer`;
  return `https://platform.api.dev.${apex}/business-transfer`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_BUSINESS_TRANSFER_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

export type RunWarning = { stepId: string; step: string; code: string; message: string };

export class BusinessTransferApiError extends Error {
  /** M4: the run gate's 409 says a takeover lease still needs the buyer's signature. */
  public leaseSignatureNeeded?: boolean;
  /** F1: complete answers 409 with the run warnings until they are acknowledged. */
  public warnings?: RunWarning[];
  constructor(public status: number, message: string, public pending?: string[], public body: Record<string, any> = {}) {
    super(message);
    if (body.leaseSignatureNeeded !== undefined) this.leaseSignatureNeeded = body.leaseSignatureNeeded === true;
    if (Array.isArray(body.warnings)) this.warnings = body.warnings;
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) throw new BusinessTransferApiError(res.status, body.error || `Request failed (${res.status})`, body.pending, body);
  return body as T;
}
const post = <T>(path: string, body?: unknown) => req<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
const put = <T>(path: string, body?: unknown) => req<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) });
const id = encodeURIComponent;

export const businessTransferApi = {
  list: (status?: TransferStatus) => req<AdminTransferList>(`/admin/transfers${status ? `?status=${status}` : ''}`),
  get: (transferId: string) => req<AdminTransferDetail>(`/admin/transfers/${id(transferId)}`),
  create: (body: CreateTransferRequest) => post<AdminTransferDetail>('/admin/transfers', body),
  sellerLocations: (tenantId: string) => req<{ locations: Array<{ locationId: string; name: string; status: string }> }>(`/admin/sellers/${id(tenantId)}/locations`),
  dryRun: (transferId: string) => post<{ queued: boolean }>(`/admin/transfers/${id(transferId)}/dry-run`),
  updateOptions: (transferId: string, body: UpdateOptionsRequest) => put<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/options`, body),
  completeStage: (transferId: string, stageId: string, note?: string) => post<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/stages/${id(stageId)}/complete`, { note }),
  undoStage: (transferId: string, stageId: string) => post<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/stages/${id(stageId)}/undo`),
  varSheet: (transferId: string, body: VarSheetRequest) => put<AdminTransferDetail & { uploadUrl?: string }>(`/admin/transfers/${id(transferId)}/var-sheet`, body),
  schedule: (transferId: string, body: ScheduleTransferRequest) => post<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/schedule`, body),
  runNow: (transferId: string, body: RunNowRequest) => post<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/run-now`, body),
  resume: (transferId: string) => post<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/resume`),
  link: (transferId: string, key: string, body: LinkDeviceRequest) => post<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/links/${id(key)}`, body),
  complete: (transferId: string, opts: { acknowledgeWarnings?: boolean } = {}) =>
    post<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/complete`, opts.acknowledgeWarnings ? { acknowledgeWarnings: true } : {}),
  cancel: (transferId: string, reason: string) => post<AdminTransferDetail>(`/admin/transfers/${id(transferId)}/cancel`, { reason }),
};
